import React, { useRef, useState, useMemo } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Tooltip,
  Switch,
  FormControlLabel,
  Typography,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import ClearIcon from "@mui/icons-material/Clear";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  FILTERABLE_COLUMNS,
  REQUIRED_COLUMN,
  formatColumnName,
  getStatusFromStage,
  capitalizeFirstLetter,
} from "../utils/columnConfig";

const ReportsFilters = ({
  filters,
  onFilterChange,
  onReset,
  latestOnly,
  onLatestOnlyChange,
  visibleColumns,
  onToggleColumn,
  columnOrder,
  onMoveColumn,
  data,
  userManufacturer,
  hasActiveFilters,
}) => {
  const anchorRef = useRef(null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  // Get distinct filter values from data
  const getDistinctFilterValues = (column) => {
    if (column === "stage") {
      const rawCodes = [
        ...new Set(data.map((r) => r[column]).filter(Boolean)),
      ].sort();
      return rawCodes.map((code) => ({
        code: code,
        text: getStatusFromStage(code),
      }));
    }

    if (column === "year") {
      return [...new Set(data.map((r) => r.year).filter(Boolean))]
        .sort()
        .reverse()
        .map((v) => ({ code: v, text: v }));
    }

    if (column === "period") {
      const availablePeriods = filters.year
        ? [
            ...new Set(
              data
                .filter((r) => r.year === filters.year)
                .map((r) => r.period)
                .filter(Boolean)
            ),
          ]
        : [...new Set(data.map((r) => r.period).filter(Boolean))];

      // Define sort order for periods
      const periodOrder = [
        "Q1",
        "Q2",
        "Q3",
        "Q4",
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];

      return availablePeriods
        .sort((a, b) => {
          const indexA = periodOrder.indexOf(a);
          const indexB = periodOrder.indexOf(b);
          // If both are in the order list, sort by index
          if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
          }
          // If only one is in the list, prioritize it
          if (indexA !== -1) return -1;
          if (indexB !== -1) return 1;
          // If neither is in the list, sort alphabetically
          return a.localeCompare(b);
        })
        .map((v) => ({ code: v, text: v }));
    }

    // For other columns (payer, job_id, content_type, source_type, manufacturer)
    return [...new Set(data.map((r) => r[column]).filter(Boolean))]
      .sort()
      .map((v) => ({ code: v, text: capitalizeFirstLetter(v) }));
  };

  const handleToggleAndClose = (col) => {
    const isRequired =
      col === REQUIRED_COLUMN ||
      (col === "manufacturer" && userManufacturer === "ciita");
    if (!isRequired) {
      onToggleColumn(col);
      setPopoverOpen(false);
    }
  };

  // Filter out manufacturer column if user is not ciita
  const displayFilterableColumns = useMemo(() => {
    return FILTERABLE_COLUMNS.filter(
      (col) => userManufacturer === "ciita" || col !== "manufacturer"
    );
  }, [userManufacturer]);

  return (
    <>
      <Box
        sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}
      >
        {/* Filter Dropdowns */}
        {displayFilterableColumns.map(
          (col) =>
            visibleColumns.has(col) && (
              <FormControl key={col} sx={{ minWidth: 150 }}>
                <InputLabel>{formatColumnName(col)}</InputLabel>
                <Select
                  value={filters[col] || ""}
                  onChange={(e) => onFilterChange(col, e.target.value)}
                  label={formatColumnName(col)}
                  disabled={col === "period" && !filters.year}
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {getDistinctFilterValues(col).map((v) => (
                    <MenuItem key={v.code} value={v.code}>
                      {v.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )
        )}

        {/* Latest Only Toggle */}
        <Tooltip title="Show only the entry with the latest stage for each unique file">
          <FormControlLabel
            control={
              <Switch
                checked={latestOnly}
                onChange={(e) => onLatestOnlyChange(e.target.checked)}
                name="latestOnly"
                color="primary"
              />
            }
            label="Latest Entries Only"
            sx={{ ml: 1, whiteSpace: "nowrap" }}
          />
        </Tooltip>

        {/* Reset Button */}
        {hasActiveFilters && (
          <Tooltip title="Reset Filters and Sorting">
            <Button
              variant="outlined"
              onClick={onReset}
              startIcon={<ClearIcon />}
            >
              Reset
            </Button>
          </Tooltip>
        )}

        {/* Column Settings */}
        <IconButton
          ref={anchorRef}
          onClick={() => setPopoverOpen((prev) => !prev)}
        >
          <SettingsIcon />
        </IconButton>
      </Box>

      {/* Column Settings Popover */}
      <Popover
        open={popoverOpen}
        anchorEl={anchorRef.current}
        onClose={() => setPopoverOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        disableRestoreFocus
      >
        <List dense sx={{ minWidth: 250 }}>
          {columnOrder.map((col, idx) => {
            // Hide manufacturer option if not ciita user
            if (col === "manufacturer" && userManufacturer !== "ciita") {
              return null;
            }

            const isRequired = col === REQUIRED_COLUMN;
            const isCiitaRequired =
              col === "manufacturer" && userManufacturer === "ciita";
            const isDisabled = isRequired || isCiitaRequired;

            return (
              <ListItem key={col} sx={{ py: 0.5 }} disableGutters>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    alignItems: "center",
                    px: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                      checked={visibleColumns.has(col)}
                      onChange={() => onToggleColumn(col)}
                      onClick={(e) => e.stopPropagation()}
                      disabled={isDisabled}
                      sx={{ mr: 1 }}
                    />
                    <ListItemText
                      primary={formatColumnName(col)}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isDisabled) handleToggleAndClose(col);
                      }}
                      sx={{ cursor: isDisabled ? "default" : "pointer" }}
                    />
                    {isDisabled && (
                      <Tooltip
                        title={
                          isRequired
                            ? "This column cannot be hidden"
                            : "Required for Ciita user"
                        }
                      >
                        <Typography
                          component="span"
                          sx={{
                            ml: 1,
                            color: "text.secondary",
                            fontWeight: "bold",
                          }}
                        >
                          *
                        </Typography>
                      </Tooltip>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <IconButton
                      size="small"
                      disabled={idx === 0}
                      onClick={(e) => {
                        e.stopPropagation();
                        onMoveColumn(col, "up");
                      }}
                      sx={{ p: 0.5 }}
                    >
                      <KeyboardArrowUpIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={idx === columnOrder.length - 1}
                      onClick={(e) => {
                        e.stopPropagation();
                        onMoveColumn(col, "down");
                      }}
                      sx={{ p: 0.5 }}
                    >
                      <KeyboardArrowDownIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </ListItem>
            );
          })}
        </List>
      </Popover>
    </>
  );
};

export default ReportsFilters;
